import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import './CustomTooltip.css';

const CustomTooltip = ({ children, content, placement = 'top', fullWidth = false, showOnMobile = false }) => {
  const [isVisible, setIsVisible] = useState(false);
  const triggerRef = useRef(null);
  const tooltipRef = useRef(null);
  const touchTimeoutRef = useRef(null);
  
  // Handle showing/hiding tooltip
  const showTooltip = () => setIsVisible(true);
  const hideTooltip = () => setIsVisible(false);
  
  // Portal container for tooltips
  const portalContainer = useRef(null);
  
  // Create a container for all tooltips if it doesn't exist
  useEffect(() => {
    if (!document.getElementById('tooltips-container')) {
      const container = document.createElement('div');
      container.id = 'tooltips-container';
      container.style.position = 'fixed';
      container.style.top = '0';
      container.style.left = '0';
      container.style.width = '100vw';
      container.style.height = '100vh';
      container.style.pointerEvents = 'none';
      container.style.zIndex = '9999';
      document.body.appendChild(container);
    }
    
    portalContainer.current = document.getElementById('tooltips-container');
    
    return () => {
      // No cleanup needed for container as other tooltips might use it
    };
  }, []);
  
  // Create and position the tooltip
  const tooltip = isVisible && portalContainer.current && createPortal(
    <div
      ref={tooltipRef}
      className={`tooltip tooltip-${placement}`}
      aria-hidden="true"
    >
      {content}
    </div>,
    portalContainer.current
  );

  // Position the tooltip when it becomes visible
  useEffect(() => {
    if (isVisible && triggerRef.current && tooltipRef.current) {
      const updatePosition = () => {
        // Add null checks to prevent error when elements are unmounted
        if (!triggerRef.current || !tooltipRef.current) return;
        
        const triggerRect = triggerRef.current.getBoundingClientRect();
        const tooltipRect = tooltipRef.current.getBoundingClientRect();
        
        // Viewport dimensions
        const viewportWidth = window.innerWidth;
        const viewportHeight = window.innerHeight;
        
        // Calculate position based on placement
        let top, left;
        
        switch (placement) {
          case 'top':
            top = triggerRect.top - tooltipRect.height - 8;
            left = triggerRect.left + (triggerRect.width / 2) - (tooltipRect.width / 2);
            break;
          case 'bottom':
            top = triggerRect.bottom + 8;
            left = triggerRect.left + (triggerRect.width / 2) - (tooltipRect.width / 2);
            break;
          case 'left':
            top = triggerRect.top + (triggerRect.height / 2) - (tooltipRect.height / 2);
            left = triggerRect.left - tooltipRect.width - 8;
            break;
          case 'right':
          default:
            top = triggerRect.top + (triggerRect.height / 2) - (tooltipRect.height / 2);
            left = triggerRect.right + 8;
            break;
        }
        
        // Constrain to viewport
        if (left < 10) left = 10;
        if (left + tooltipRect.width > viewportWidth - 10) {
          left = viewportWidth - tooltipRect.width - 10;
        }
        
        if (top < 10) top = 10;
        if (top + tooltipRect.height > viewportHeight - 10) {
          top = viewportHeight - tooltipRect.height - 10;
        }
        
        // Apply position
        tooltipRef.current.style.top = `${top}px`;
        tooltipRef.current.style.left = `${left}px`;
      };
      
      // Position initially and on resize/scroll
      // Delay initial positioning slightly to allow for proper rendering
      setTimeout(updatePosition, 10);
      window.addEventListener('resize', updatePosition);
      window.addEventListener('scroll', updatePosition);
      
      return () => {
        window.removeEventListener('resize', updatePosition);
        window.removeEventListener('scroll', updatePosition);
      };
    }
  }, [isVisible, placement]);

  // Check if on mobile device
  const isMobile = window.innerWidth <= 768;
  
  // Handle mobile touch events with better control
  const handleTouchStart = (e) => {
    if (showOnMobile && isMobile) {
      e.preventDefault(); // Prevent default touch behavior
      
      // Clear any existing timeout
      if (touchTimeoutRef.current) {
        clearTimeout(touchTimeoutRef.current);
      }
      
      setIsVisible(true);
    }
  };
  
  const handleTouchEnd = (e) => {
    if (showOnMobile && isMobile) {
      e.preventDefault();
      
      // Use timeout to keep tooltip visible for a moment after touch
      touchTimeoutRef.current = setTimeout(() => {
        setIsVisible(false);
      }, 1500); // Show for 1.5 seconds before hiding
    }
  };
  
  // Clean up timeout on unmount
  useEffect(() => {
    return () => {
      if (touchTimeoutRef.current) {
        clearTimeout(touchTimeoutRef.current);
      }
    };
  }, []);

  return (
    <div
      ref={triggerRef}
      className={`tooltip-trigger ${fullWidth ? 'tooltip-full-width' : ''}`}
      onMouseEnter={!isMobile || showOnMobile ? showTooltip : undefined}
      onMouseLeave={!isMobile || showOnMobile ? hideTooltip : undefined}
      onFocus={!isMobile || showOnMobile ? showTooltip : undefined}
      onBlur={!isMobile || showOnMobile ? hideTooltip : undefined}
      onClick={showOnMobile && isMobile ? handleTouchStart : undefined}
      onTouchStart={showOnMobile && isMobile ? handleTouchStart : undefined}
      onTouchEnd={showOnMobile && isMobile ? handleTouchEnd : undefined}
    >
      {children}
      {tooltip}
    </div>
  );
};

export default CustomTooltip;
